import type { ThunkDispatch, UnknownAction } from '@reduxjs/toolkit';
import { createApi } from '@reduxjs/toolkit/query/react';

import {
  DISCOVERY_PAGE_REDUCER_KEY,
  getBaseUrlObNextGen,
  getObNextGenApiUrlLists,
  getObNextgenTagTypes,
} from '../../../constants/obNextGenConstants';
import {
  setDiscoveryConnectionListResult,
  setGenerateTemplateResult,
} from '../../../redux/globalSlice';
import { axiosBaseQuery } from '../../../services/baseQuery';

import {
  type ApplicationApiSuccessResponse,
  type ConnectionListQuery,
  type ConnectionListResponse,
  type ConnectionPayload,
  type ConnectionResponse,
  type GenerateFormSuccessResponse,
  type GenerateFormTemplate,
} from './ApplicationCard';
import {
  setDiscoveryConnectionListError,
  setDiscoveryConnectionListLoading,
} from './ConnectionListSlice';
import {
  setDiscoveryExistingConnectionError,
  setDiscoveryExistingConnectionLoading,
  setDiscoveryExistingConnectionResult,
} from './DiscoveryExistingConnectionSlice';
import {
  setDiscoverySaveConnectionError,
  setDiscoverySaveConnectionLoading,
  setDiscoverySaveConnectionResult,
} from './DiscoverySaveConnectionSlice';
import {
  setExistingConnectionDetailsError,
  setExistingConnectionDetailsLoading,
} from './ExistingConnectionSlice';
import {
  setGenerateTemplateError,
  setGenerateTemplateLoading,
} from './GenerateTemplateSlice';

// Testable utility functions
export const buildConnectionListUrl = (
  baseUrl: string,
  params: ConnectionListQuery
): string => {
  let url = `${baseUrl}?appTypeKey=${params.appTypeKey}`;

  if (params.search) {
    url += `&search=${params.search}`;
  }

  if (params.page !== undefined && params.size) {
    url += `&page=${params.page}&size=${params.size}`;
  }

  return url;
};

export const extractErrorMessage = (err: unknown): string => {
  if (err && typeof err === 'object' && 'message' in err) {
    return (err as { message: string }).message;
  }

  return 'Unknown error';
};

export const extractSaveConnectionErrorMessage = (err: unknown): string => {
  if (err && typeof err === 'object' && 'error' in err) {
    const errorObj = (err as { error: { data?: { message?: string } } }).error;

    if (errorObj.data?.message) {
      try {
        const messageStr = errorObj.data.message;
        const errorMsgMatch = messageStr.match(/errorMessage=([^}]+)/);

        return errorMsgMatch ? errorMsgMatch[1] : messageStr;
      } catch {
        return errorObj.data.message;
      }
    }
  }

  return 'Unknown error';
};

type AppDispatch = ThunkDispatch<unknown, unknown, UnknownAction>;

export const handleConnectionListSuccess = (
  dispatch: AppDispatch,
  data: ConnectionListResponse
) => {
  dispatch(setDiscoveryConnectionListResult(data));
};

export const handleConnectionListError = (
  dispatch: AppDispatch,
  err: unknown
) => {
  const errorMessage = extractErrorMessage(err);

  dispatch(setDiscoveryConnectionListError(errorMessage));
};

export const handleFormTemplateSuccess = (
  dispatch: AppDispatch,
  data: GenerateFormSuccessResponse
) => {
  dispatch(setGenerateTemplateResult(data));
};

export const handleFormTemplateError = (
  dispatch: AppDispatch,
  err: unknown
) => {
  const errorMessage = extractErrorMessage(err);

  dispatch(setGenerateTemplateError(errorMessage));
};

export const handleSaveConnectionSuccess = (
  dispatch: AppDispatch,
  data: ConnectionResponse,
  hasExternalKey: boolean
) => {
  if (hasExternalKey) {
    dispatch(setDiscoveryExistingConnectionResult(data));
  } else {
    dispatch(setDiscoverySaveConnectionResult(data));
  }
};

export const handleSaveConnectionError = (
  dispatch: AppDispatch,
  err: unknown,
  hasExternalKey: boolean
) => {
  const errorMessage = extractSaveConnectionErrorMessage(err);

  if (hasExternalKey) {
    dispatch(setDiscoveryExistingConnectionError(errorMessage));
    dispatch(setDiscoveryExistingConnectionResult(null));
  } else {
    dispatch(setDiscoverySaveConnectionError(errorMessage));
    dispatch(setDiscoverySaveConnectionResult(null));
  }
};

export const handleExistingConnectionError = (
  dispatch: AppDispatch,
  err: unknown
) => {
  const errorMessage = extractErrorMessage(err);

  dispatch(setExistingConnectionDetailsError(errorMessage));
};

const { DISCOVERY_PAGE } = getObNextgenTagTypes();
const {
  GET_APPLICATIONS,
  GET_FORM_TEMPLATE,
  SAVE_CONNECTION,
  GET_CONNECTION_LIST,
} = getObNextGenApiUrlLists();

export const discoveryApplicationApi = createApi({
  reducerPath: DISCOVERY_PAGE_REDUCER_KEY,
  baseQuery: axiosBaseQuery({
    baseUrl: getBaseUrlObNextGen(),
  }),
  tagTypes: [
    DISCOVERY_PAGE.GET_APPLICATIONS,
    DISCOVERY_PAGE.FORM_TEMPLATE,
    DISCOVERY_PAGE.SAVE_CONNECTION,
    DISCOVERY_PAGE.GET_CONNECTION_LIST,
  ],
  endpoints: (builder) => ({
    getDiscoveryApplications: builder.query<
      ApplicationApiSuccessResponse,
      void
    >({
      query: () => ({
        url: GET_APPLICATIONS,
        method: 'GET',
      }),
      providesTags: [DISCOVERY_PAGE.GET_APPLICATIONS],
    }),
    getConnectionList: builder.query<
      ConnectionListResponse,
      ConnectionListQuery
    >({
      query: (params) => ({
        url: buildConnectionListUrl(GET_CONNECTION_LIST, params),
        method: 'GET',
      }),
      keepUnusedDataFor: 0,
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        dispatch(setDiscoveryConnectionListLoading(true));
        dispatch(setDiscoveryConnectionListError(null));

        try {
          const { data } = await queryFulfilled;

          handleConnectionListSuccess(dispatch, data);
        } catch (err: unknown) {
          handleConnectionListError(dispatch, err);
        } finally {
          dispatch(setDiscoveryConnectionListLoading(false));
        }
      },
    }),
    formTemplate: builder.mutation<
      GenerateFormSuccessResponse,
      GenerateFormTemplate
    >({
      query: (body) => ({
        url: GET_FORM_TEMPLATE,
        method: 'POST',
        data: body,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      invalidatesTags: [DISCOVERY_PAGE.FORM_TEMPLATE],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        dispatch(setGenerateTemplateLoading(true));
        dispatch(setGenerateTemplateError(null));

        try {
          const { data } = await queryFulfilled;

          handleFormTemplateSuccess(dispatch, data);
        } catch (err: unknown) {
          handleFormTemplateError(dispatch, err);
        } finally {
          dispatch(setGenerateTemplateLoading(false));
        }
      },
    }),
    discoverySaveConnection: builder.mutation<
      ConnectionResponse,
      ConnectionPayload
    >({
      query: (body) => ({
        url: SAVE_CONNECTION,
        method: 'POST',
        data: body,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      invalidatesTags: [DISCOVERY_PAGE.SAVE_CONNECTION],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        const hasExternalKey = Boolean(_arg.externalConnectionKey);

        if (hasExternalKey) {
          dispatch(setDiscoveryExistingConnectionLoading(true));
          dispatch(setDiscoveryExistingConnectionError(null));
        } else {
          dispatch(setDiscoverySaveConnectionLoading(true));
          dispatch(setDiscoverySaveConnectionError(null));
        }

        try {
          const { data } = await queryFulfilled;

          handleSaveConnectionSuccess(dispatch, data, hasExternalKey);
        } catch (err: unknown) {
          handleSaveConnectionError(dispatch, err, hasExternalKey);
        } finally {
          if (hasExternalKey) {
            dispatch(setDiscoveryExistingConnectionLoading(false));
          } else {
            dispatch(setDiscoverySaveConnectionLoading(false));
          }
        }
      },
    }),
    existingConnectionApi: builder.mutation<
      GenerateFormSuccessResponse,
      GenerateFormTemplate
    >({
      query: (body) => ({
        url: GET_FORM_TEMPLATE,
        method: 'POST',
        data: body,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        dispatch(setExistingConnectionDetailsLoading(true));
        dispatch(setExistingConnectionDetailsError(null));

        try {
          const { data } = await queryFulfilled;

          dispatch(setGenerateTemplateResult(data));
        } catch (err: unknown) {
          handleExistingConnectionError(dispatch, err);
        } finally {
          dispatch(setExistingConnectionDetailsLoading(false));
        }
      },
    }),
  }),
});

export const {
  useGetDiscoveryApplicationsQuery,
  useGetConnectionListQuery,
  useFormTemplateMutation,
  useDiscoverySaveConnectionMutation,
  useExistingConnectionApiMutation,
} = discoveryApplicationApi;
